import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Copy, ExternalLink, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { authApi, usd, QUOTE_STATUS_COLORS } from "@/lib/auth";
import { AdminHeader } from "@/pages/admin/Dashboard";

const inputCls =
  "w-full bg-white/[0.04] border hairline rounded-sm px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#A7D7A0]/60";
const labelCls = "block text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white/40 mb-1.5";

const suggestedPrice = (unitDirect) => Math.ceil((unitDirect / 0.8) / 5) * 5;

export default function QuoteComposer() {
  const { leadId, quoteId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [lead, setLead] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [quote, setQuote] = useState(null); // saved server quote (edit mode)
  const [lines, setLines] = useState([]);
  const [otherCosts, setOtherCosts] = useState("");
  const [otherNote, setOtherNote] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    authApi.me().then(setUser).catch(() => navigate("/admin", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    authApi.suppliers().then((d) => setSuppliers(d.suppliers));
    if (quoteId) {
      authApi.quote(quoteId).then((q) => {
        setQuote(q);
        setLines(q.lines.map((l) => ({ supplier_id: l.supplier_id, qty: l.qty, unit_price: l.unit_price })));
        setOtherCosts(String(q.other_costs || ""));
        setOtherNote(q.other_costs_note || "");
        setNeedsReview(!!q.needs_review);
        setNotes(q.notes || "");
        if (q.status === "sent") setSent(q);
        authApi.lead(q.lead_id).then(setLead);
      }).catch(() => navigate("/admin/dashboard"));
    } else if (leadId) {
      authApi.lead(leadId).then(setLead).catch(() => navigate("/admin/dashboard"));
      setLines([{ supplier_id: "", qty: 1, unit_price: "" }]);
    }
  }, [user, leadId, quoteId, navigate]);

  const byId = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.supplier_id, s])), [suppliers]);

  const computed = useMemo(() => {
    let selling = 0, direct = 0;
    for (const l of lines) {
      const sup = byId[l.supplier_id];
      if (!sup) continue;
      const unitDirect = sup.wholesale_cost + sup.freight_estimate + sup.install_estimate;
      selling += (Number(l.unit_price) || 0) * l.qty;
      direct += unitDirect * l.qty;
    }
    direct += Number(otherCosts) || 0;
    const margin = selling > 0 ? ((selling - direct) / selling) * 100 : 0;
    return { selling, direct, margin };
  }, [lines, otherCosts, byId]);

  const escalations = useMemo(() => {
    const r = [];
    if (computed.selling > 10000) r.push("Exceeds the $10,000 automatic contract limit");
    if (computed.selling > 0 && computed.margin < 20) r.push(`Gross margin ${computed.margin.toFixed(1)}% is below the 20% minimum`);
    if (needsReview) r.push("Flagged for electrical design / permitting / engineering review");
    return r;
  }, [computed, needsReview]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const pickProduct = (i, supplier_id) => {
    const sup = byId[supplier_id];
    const patch = { supplier_id };
    if (sup) patch.unit_price = suggestedPrice(sup.wholesale_cost + sup.freight_estimate + sup.install_estimate);
    setLine(i, patch);
  };

  const payload = () => ({
    lines: lines.filter((l) => l.supplier_id).map((l) => ({ supplier_id: l.supplier_id, qty: Number(l.qty) || 1, unit_price: Number(l.unit_price) || 0 })),
    other_costs: Number(otherCosts) || 0,
    other_costs_note: otherNote,
    needs_review: needsReview,
    notes,
  });

  const save = async () => {
    const p = payload();
    if (p.lines.length === 0) { toast.error("Add at least one product line"); return null; }
    setSaving(true);
    try {
      const q = quoteId ? await authApi.updateQuote(quoteId, p) : await authApi.createQuote(leadId, p);
      toast.success("Quote saved");
      if (!quoteId) navigate(`/admin/quotes/${q.quote_id}`, { replace: true });
      else setQuote({ ...quote, ...q });
      return q;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save quote");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    const q = quote || (await save());
    if (!q) return;
    try {
      await authApi.approveQuote(q.quote_id || quoteId);
      setQuote((prev) => ({ ...prev, approved: true }));
      toast.success("Quote manually approved");
    } catch { toast.error("Approval failed"); }
  };

  const send = async () => {
    const q = quote || (await save());
    if (!q) return;
    try {
      const res = await authApi.sendQuote(q.quote_id || quoteId);
      setSent(res);
      setQuote((prev) => ({ ...prev, status: "sent" }));
      toast.success("Proposal is live — share the link with your customer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send quote");
    }
  };

  if (!user || !lead) return <main className="min-h-screen bg-void" />;

  const isSent = quote?.status === "sent";
  const approved = !!quote?.approved;
  const proposalLink = `${window.location.origin}/proposal/${quoteId}`;

  return (
    <main className="min-h-screen bg-void" data-testid="quote-composer">
      <AdminHeader user={user} />
      <div className="px-4 sm:px-8 max-w-6xl mx-auto py-10">
        <button onClick={() => navigate(`/admin/leads/${lead.lead_id}`)} data-testid="composer-back-btn" className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back to {lead.company}
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">Quote Composer</h1>
            <p className="text-muted-green text-sm mt-1">{lead.company} · {lead.full_name} · {lead.location}</p>
          </div>
          {quote && (
            <span className={`px-3 py-1.5 rounded-sm border text-xs ${QUOTE_STATUS_COLORS[quote.status] || QUOTE_STATUS_COLORS.draft}`} data-testid="quote-status-badge">
              {isSent ? "Sent" : "Draft"}{approved && !isSent ? " · Approved" : ""}
            </span>
          )}
        </div>

        {isSent ? (
          <div className="bg-card-dark border hairline rounded-md p-8" data-testid="quote-sent-panel">
            <CheckCircle2 size={22} className="text-[#A7D7A0] mb-4" />
            <h2 className="font-display font-semibold text-xl">Proposal Sent</h2>
            <p className="text-muted-green text-sm mt-2 mb-6">Total {usd(quote.selling_price)} · margin {quote.gross_margin_pct}% · valid 30 days from send date.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => { navigator.clipboard.writeText(proposalLink); toast.success("Link copied"); }} data-testid="copy-proposal-link-btn" className="inline-flex items-center justify-center gap-2 bg-[#A7D7A0] text-[#101214] font-semibold text-sm px-5 py-3 rounded-sm hover:bg-white transition-colors">
                <Copy size={14} /> Copy Proposal Link
              </button>
              <Link to={`/proposal/${quoteId}`} target="_blank" data-testid="open-proposal-btn" className="inline-flex items-center justify-center gap-2 border hairline text-sm text-white/70 hover:text-white px-5 py-3 rounded-sm transition-colors">
                <ExternalLink size={14} /> Open Proposal
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-6">
              <div className="bg-card-dark border hairline rounded-md p-7">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0]">Line Items</h2>
                  <button onClick={() => setLines([...lines, { supplier_id: "", qty: 1, unit_price: "" }])} data-testid="add-line-btn" className="inline-flex items-center gap-1.5 text-xs text-[#A7D7A0] hover:text-white transition-colors">
                    <Plus size={13} /> Add line
                  </button>
                </div>
                <div className="space-y-4">
                  {lines.map((l, i) => {
                    const sup = byId[l.supplier_id];
                    const unitDirect = sup ? sup.wholesale_cost + sup.freight_estimate + sup.install_estimate : 0;
                    return (
                      <div key={i} className="grid grid-cols-12 gap-3 items-end border-b hairline pb-4" data-testid={`quote-line-${i}`}>
                        <div className="col-span-12 sm:col-span-5">
                          <label className={labelCls}>Product</label>
                          <select data-testid={`line-product-${i}`} value={l.supplier_id} onChange={(e) => pickProduct(i, e.target.value)} className={inputCls}>
                            <option value="">Select a supplier product…</option>
                            {suppliers.map((s) => (
                              <option key={s.supplier_id} value={s.supplier_id}>
                                {s.supplier_name} — {s.product} ({usd(s.wholesale_cost + s.freight_estimate + s.install_estimate)} direct)
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <label className={labelCls}>Qty</label>
                          <input data-testid={`line-qty-${i}`} type="number" min="1" className={inputCls} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                        </div>
                        <div className="col-span-8 sm:col-span-4">
                          <label className={labelCls}>Unit Selling Price ($){sup ? ` · direct ${usd(unitDirect)}` : ""}</label>
                          <input data-testid={`line-price-${i}`} type="number" min="0" step="0.01" className={inputCls} value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} />
                        </div>
                        <div className="col-span-12 sm:col-span-1 text-right">
                          <button onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`line-remove-${i}`} className="text-white/30 hover:text-red-400 p-2"><Trash2 size={15} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div>
                    <label className={labelCls}>Other Direct Costs ($)</label>
                    <input data-testid="other-costs-input" type="number" min="0" step="0.01" className={inputCls} value={otherCosts} onChange={(e) => setOtherCosts(e.target.value)} placeholder="e.g. permits, lifts, misc materials" />
                  </div>
                  <div>
                    <label className={labelCls}>Other Costs Note</label>
                    <input data-testid="other-costs-note" className={inputCls} value={otherNote} onChange={(e) => setOtherNote(e.target.value)} />
                  </div>
                </div>

                <label className="mt-6 flex items-center gap-3 cursor-pointer" data-testid="needs-review-toggle">
                  <input type="checkbox" checked={needsReview} onChange={(e) => setNeedsReview(e.target.checked)} className="accent-[#A7D7A0] w-4 h-4" />
                  <span className="text-sm text-white/70">Requires electrical design / permitting / engineering review</span>
                </label>

                <div className="mt-6">
                  <label className={labelCls}>Proposal Notes (visible to customer)</label>
                  <textarea data-testid="quote-notes-input" rows={3} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Scope summary, phasing, what's included…" />
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-card-dark border hairline rounded-md p-7 sticky top-24" data-testid="quote-totals-panel">
                <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0] mb-6">Pricing Check</h2>
                <dl className="space-y-4 text-sm">
                  <div className="flex justify-between"><dt className="text-white/50">Direct costs</dt><dd className="text-white" data-testid="totals-direct">{usd(computed.direct)}</dd></div>
                  <div className="flex justify-between"><dt className="text-white/50">Selling price</dt><dd className="text-white font-semibold" data-testid="totals-selling">{usd(computed.selling)}</dd></div>
                  <div className="flex justify-between border-t hairline pt-4">
                    <dt className="text-white/50">Gross margin</dt>
                    <dd className={`font-semibold ${computed.margin >= 20 ? "text-[#A7D7A0]" : "text-red-400"}`} data-testid="totals-margin">{computed.margin.toFixed(1)}%</dd>
                  </div>
                </dl>

                {escalations.length > 0 && (
                  <div className="mt-6 bg-[#FFC857]/10 border border-[#FFC857]/30 rounded-sm p-4" data-testid="escalation-panel">
                    <p className="flex items-center gap-2 text-[#FFC857] text-xs font-semibold mb-2"><ShieldAlert size={14} /> Manual approval required</p>
                    <ul className="text-xs text-white/60 space-y-1.5">
                      {escalations.map((r) => <li key={r}>· {r}</li>)}
                    </ul>
                  </div>
                )}

                <div className="mt-7 space-y-3">
                  <button onClick={save} disabled={saving} data-testid="save-quote-btn" className="w-full bg-white text-[#101214] font-semibold text-sm px-5 py-3 rounded-sm hover:bg-[#A7D7A0] transition-colors disabled:opacity-50">
                    {saving ? "Saving…" : quoteId ? "Save Changes" : "Save Draft"}
                  </button>
                  {escalations.length > 0 && !approved && (
                    <button onClick={approve} data-testid="approve-quote-btn" className="w-full border border-[#FFC857]/50 text-[#FFC857] font-semibold text-sm px-5 py-3 rounded-sm hover:bg-[#FFC857]/10 transition-colors">
                      Approve Manually
                    </button>
                  )}
                  <button
                    onClick={send}
                    disabled={saving || (escalations.length > 0 && !approved)}
                    data-testid="send-quote-btn"
                    className="w-full bg-[#A7D7A0] text-[#101214] font-semibold text-sm px-5 py-3 rounded-sm hover:bg-white transition-colors disabled:opacity-40"
                  >
                    Send Proposal
                  </button>
                  {escalations.length > 0 && !approved && (
                    <p className="text-[10px] text-white/35 leading-relaxed">Sending is locked until this quote is manually approved — Fairlume never auto-issues binding quotes.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
